// src/pages/HotelDetails.jsx — Broadway Holidays — Hotel Only (no flights)
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  getHotelDetails, getDescriptionAndInfo, getHotelPhotos,
  getRoomsWithAvailability, getHotelFacilities, getHotelReviews,
  getHotelPolicies, getHotelReviewScores,
  searchCarRentals, searchCarLocation,
  searchTaxis, searchTaxiLocation,
  searchAttractions, searchAttractionLocation,
  extractName, extractDescription, extractRoomBoard,
  fixPhoto, ratingLabel, ratingBubbles, _s,
} from "../services/hotelApi";

const PJS = {fontFamily:"'Plus Jakarta Sans',sans-serif"};
const DS  = {fontFamily:"'DM Sans',sans-serif"};
const NAV = "#0B1F3A";
const GRN = "#059669";

const addD  = (d,n) => { const x=new Date(d); x.setDate(x.getDate()+n); return x; };
const toISO = d => { try{return(d instanceof Date?d:new Date(d)).toISOString().split("T")[0];}catch{return "";} };
const fDS   = d => { try{return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});}catch{return String(d||"");} };
const fDL   = d => { try{return new Date(d).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"});}catch{return String(d||"");} };

const BC = {
  "All Inclusive":  {bg:"#064e3b",txt:"#fff"},
  "Half Board":     {bg:"#92400e",txt:"#fff"},
  "Full Board":     {bg:"#4c1d95",txt:"#fff"},
  "Bed & Breakfast":{bg:"#1e3a8a",txt:"#fff"},
  "Self Catering":  {bg:"#374151",txt:"#fff"},
  "Room Only":      {bg:"#4b5563",txt:"#fff"},
};
const MEAL_MAP = {"0":"Room Only","1":"Bed & Breakfast","2":"Bed & Breakfast","3":"Half Board","4":"Full Board","5":"All Inclusive","9":"All Inclusive","10":"All Inclusive","12":"Self Catering","14":"Half Board","20":"Full Board"};
const BOARD_DESC = {"All Inclusive":"All meals, drinks & snacks included","Half Board":"Breakfast + dinner included","Full Board":"All three meals included","Bed & Breakfast":"Breakfast included","Self Catering":"Kitchen facilities available","Room Only":"No meals included"};

function getBoardColor(b){return BC[b]||BC["Room Only"];}

function detectBoard(r){
  if(!r)return"";
  const raw=r?.mealplan||r?.meal_plan||r?.board||r?.board_type||r?.mealPlan||r?.block?.[0]?.mealplan||r?.rateplans?.[0]?.mealplan||r?.room_data_trans?.mealplan||"";
  const m=_s(raw).toLowerCase();
  if(m.includes("all incl"))   return"All Inclusive";
  if(m.includes("half board")||m==="hb")return"Half Board";
  if(m.includes("full board")||m==="fb")return"Full Board";
  if(m.includes("breakfast")||m.includes("b&b"))return"Bed & Breakfast";
  if(m.includes("self cater")||m.includes("kitchen"))return"Self Catering";
  if(m.includes("room only")||m.includes("no meal")||m==="ro")return"Room Only";
  const mid=r?.meal_type_id??r?.mealTypeId??r?.block?.[0]?.meal_type_id;
  if(mid!=null&&MEAL_MAP[String(mid)])return MEAL_MAP[String(mid)];
  const nm=_s(r?.room_name||r?.name||"").toLowerCase();
  if(nm.includes("all incl"))   return"All Inclusive";
  if(nm.includes("half board")) return"Half Board";
  if(nm.includes("breakfast"))  return"Bed & Breakfast";
  return"";
}

function extractPrice(r){
  return Number(r?.price?.gross||r?.min_price||r?.cheapest_price?.value||r?.product_price_breakdown?.gross_amount?.value||r?.composite_price_breakdown?.gross_amount?.value||r?.block?.[0]?.price?.gross||0);
}

const I = {
  Star:  ({s=13,f="#f59e0b"}) => <svg width={s} height={s} viewBox="0 0 24 24" fill={f}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  Pin:   ({s=12,c="#64748b"}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Cal:   ({s=12}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Check: ({s=11,c="#fff"})    => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Right: ({s=14,c="currentColor"}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  Phone: ({s=14}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.91-1.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  WApp:  ({s=14}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.985-1.349A9.954 9.954 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>,
  Chev:  ({s=10,up=false}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{transform:up?"rotate(180deg)":"none",transition:"transform .2s"}}><polyline points="6 9 12 15 18 9"/></svg>,
};

function Sk({h=14,w="100%",r=6,mb=0}){return <div style={{height:h,width:w,borderRadius:r,marginBottom:mb,background:"linear-gradient(90deg,#f1f5f9 25%,#e9eef5 50%,#f1f5f9 75%)",backgroundSize:"200% 100%",animation:"sk 1.5s infinite"}}/>;}

function Bubbles({score,sz=11}){
  if(!score)return null;
  const n=ratingBubbles(score),full=Math.floor(n),half=(n%1)>=.25&&(n%1)<.75?1:0,empty=5-full-half;
  return <span style={{display:"inline-flex",alignItems:"center",gap:2}}>
    {[...Array(full)].map((_,i)=><span key={"f"+i}style={{width:sz,height:sz,borderRadius:"50%",background:"#34e0a1",display:"inline-block"}}/>)}
    {half===1&&<span style={{width:sz,height:sz,borderRadius:"50%",background:"linear-gradient(90deg,#34e0a1 50%,#d1fae5 50%)",display:"inline-block"}}/>}
    {[...Array(empty)].map((_,i)=><span key={"e"+i}style={{width:sz,height:sz,borderRadius:"50%",background:"#d1fae5",border:"1.5px solid #34e0a1",display:"inline-block",boxSizing:"border-box"}}/>)}
  </span>;
}

function BoardBadge({board}){
  const b=BC[board]||BC["Room Only"];
  return <span style={{display:"inline-block",padding:"3px 10px",borderRadius:5,fontSize:11,fontWeight:700,background:b.bg,color:b.txt,...DS}}>{board}</span>;
}

/* ── Gallery ──────────────────────────────────────────────── */
function Gallery({photos,name}){
  const [idx,setIdx]=useState(0);
  const [lb,setLb]=useState(false);
  const [errs,setErrs]=useState({});
  const list=photos?.length?photos:[];
  if(!list.length)return null;
  const safe=i=>errs[i]?list[0]:(list[i]||list[0]);
  return(<>
    <div style={{borderRadius:12,overflow:"hidden",position:"relative",background:"#000",marginBottom:0}}>
      <div style={{height:"min(450px,52vw)",position:"relative",cursor:"pointer"}} onClick={()=>setLb(true)}>
        <img src={safe(idx)} alt={name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} onError={()=>setErrs(e=>({...e,[idx]:true}))}/>
        {list.length>1&&<>
          <button onClick={e=>{e.stopPropagation();setIdx(i=>(i-1+list.length)%list.length);}} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,.92)",border:"none",width:36,height:36,borderRadius:"50%",fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
          <button onClick={e=>{e.stopPropagation();setIdx(i=>(i+1)%list.length);}} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,.92)",border:"none",width:36,height:36,borderRadius:"50%",fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
        </>}
        <div style={{position:"absolute",bottom:10,right:12,display:"flex",gap:6}}>
          <span style={{background:"rgba(0,0,0,.6)",color:"#fff",fontSize:11,padding:"3px 9px",borderRadius:16,...DS}}>{idx+1}/{list.length}</span>
          <button onClick={e=>{e.stopPropagation();setLb(true);}} style={{background:"rgba(0,0,0,.6)",border:"none",color:"#fff",fontSize:11,padding:"3px 10px",borderRadius:16,cursor:"pointer",...DS}}>All photos</button>
        </div>
      </div>
      {list.length>1&&<div style={{display:"flex",gap:2,padding:"4px",background:"#111",overflowX:"auto",scrollbarWidth:"none"}}>
        {list.slice(0,14).map((_,i)=>(
          <div key={i} onClick={()=>setIdx(i)} style={{flexShrink:0,width:64,height:44,borderRadius:5,overflow:"hidden",cursor:"pointer",opacity:i===idx?1:.5,outline:i===idx?"2px solid #fff":"none",outlineOffset:-2}}>
            <img src={safe(i)} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} onError={()=>setErrs(e=>({...e,[i]:true}))}/>
          </div>
        ))}
        {list.length>14&&<div style={{flexShrink:0,width:64,height:44,borderRadius:5,background:"rgba(255,255,255,.1)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11,...DS}}>+{list.length-14}</div>}
      </div>}
    </div>
    {lb&&<div onClick={()=>setLb(false)} style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.95)",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <button onClick={e=>{e.stopPropagation();setIdx(i=>(i-1+list.length)%list.length);}} style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,.1)",border:"none",color:"#fff",width:48,height:48,borderRadius:"50%",fontSize:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
      <img src={safe(idx)} alt="" onClick={e=>e.stopPropagation()} style={{maxWidth:"90vw",maxHeight:"88vh",objectFit:"contain",borderRadius:6}}/>
      <button onClick={e=>{e.stopPropagation();setIdx(i=>(i+1)%list.length);}} style={{position:"absolute",right:16,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,.1)",border:"none",color:"#fff",width:48,height:48,borderRadius:"50%",fontSize:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
      <button onClick={()=>setLb(false)} style={{position:"absolute",top:14,right:14,background:"rgba(255,255,255,.15)",border:"none",color:"#fff",width:38,height:38,borderRadius:"50%",fontSize:20,cursor:"pointer"}}>×</button>
      <div style={{position:"absolute",bottom:14,color:"rgba(255,255,255,.6)",fontSize:12,...DS}}>{idx+1} / {list.length}</div>
    </div>}
  </>);
}

/* ── Room Card ────────────────────────────────────────────── */
function RoomCard({room,selected,onSelect,nights,guests,boardFromUrl}){
  const name   = room._name;
  const apiPr  = room._price;
  const board  = room._board || boardFromUrl || "Room Only";
  const photos = room._photos||[];
  const beds   = room._beds;
  const maxOcc = room._maxOcc;
  const extras = room._extras||[];
  const gCnt   = Math.max(1,guests);
  const hotelPP= apiPr>0 ? Math.ceil((apiPr/gCnt)*1.05) : null;
  const deposit= hotelPP ? Math.max(19,Math.round(hotelPP*0.08)) : null;
  const bclr   = BC[board]||BC["Room Only"];

  return(
    <div onClick={onSelect} style={{border:`2px solid ${selected?"#0B1F3A":"#e2e8f0"}`,borderRadius:12,overflow:"hidden",cursor:"pointer",marginBottom:10,background:selected?"#f8fafc":"#fff",transition:"border-color .15s",boxShadow:selected?"0 3px 14px rgba(11,31,58,.1)":"none"}}>
      <div style={{display:"flex"}}>
        <div style={{width:120,flexShrink:0,background:"#f1f5f9",position:"relative"}}>
          {photos[0]
            ?<img src={photos[0]} alt={name} style={{width:"100%",height:"100%",minHeight:120,objectFit:"cover",display:"block"}} onError={e=>e.target.style.display="none"}/>
            :<div style={{minHeight:120,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>🏨</div>
          }
        </div>
        <div style={{flex:1,padding:"12px 14px",minWidth:0}}>
          <div style={{fontWeight:800,fontSize:14,color:NAV,...PJS,marginBottom:5}}>{name}</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>
            <BoardBadge board={board}/>
            {beds&&<span style={{fontSize:11,color:"#64748b",background:"#f1f5f9",padding:"2px 8px",borderRadius:4,...DS}}>{beds}</span>}
            {maxOcc>0&&<span style={{fontSize:11,color:"#64748b",background:"#f1f5f9",padding:"2px 8px",borderRadius:4,...DS}}>Max {maxOcc} guests</span>}
          </div>
          <div style={{fontSize:12,color:"#475569",...DS,marginBottom:extras.length?6:0}}>{BOARD_DESC[board]||"No meals included"}</div>
          {extras.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:4}}>
            {extras.slice(0,4).map((e,i)=><span key={i} style={{fontSize:10,color:"#047857",background:"#ecfdf5",padding:"1px 7px",borderRadius:4,...DS}}>✓ {e}</span>)}
          </div>}
          {selected&&<div style={{marginTop:6,display:"flex",alignItems:"center",gap:4,fontSize:11,color:GRN,fontWeight:700,...DS}}>
            <span style={{width:15,height:15,borderRadius:"50%",background:GRN,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><I.Check s={8}/></span>
            Selected
          </div>}
        </div>
        {/* Price */}
        <div style={{width:150,flexShrink:0,borderLeft:"1px solid #f1f5f9",padding:"12px 13px",display:"flex",flexDirection:"column",alignItems:"flex-end",justifyContent:"space-between",background:"#fafafa"}}>
          <div style={{textAlign:"right"}}>
            {deposit&&<div style={{fontSize:10,color:"#94a3b8",marginBottom:2,...DS}}>Deposit <strong style={{color:NAV}}>£{deposit}</strong></div>}
            {hotelPP
              ?<>
                <div style={{fontSize:10,color:"#94a3b8",...DS}}>From</div>
                <div style={{fontSize:26,fontWeight:900,color:NAV,lineHeight:1,...PJS}}>£{hotelPP}</div>
                <div style={{fontSize:10,color:"#64748b",...DS}}>per person</div>
                <div style={{fontSize:9,color:"#94a3b8",marginTop:1,...DS}}>hotel only</div>
              </>
              :<div style={{fontSize:11,color:"#94a3b8",...DS,textAlign:"right"}}>Call for<br/>price</div>
            }
          </div>
          <button style={{marginTop:6,width:"100%",padding:"7px 0",background:selected?"#059669":NAV,color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",...DS}}
            onClick={e=>{e.stopPropagation();onSelect();}}>
            {selected?"✓ Selected":"Select"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Booking Panel (hotel only) ───────────────────────────── */
function BookingPanel({name,ci,co,nights,board,hotelPP,pax,onBook}){
  const deposit = hotelPP ? Math.max(19,Math.round(hotelPP*0.08)) : 0;
  const total   = hotelPP ? hotelPP*Math.max(1,pax) : 0;
  const bclr    = BC[board]||BC["Room Only"];

  return(
    <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 20px rgba(11,31,58,.1)"}}>
      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${NAV} 0%,#1e3a5c 100%)`,color:"#fff",padding:"18px 20px"}}>
        <div style={{fontSize:10,letterSpacing:".08em",textTransform:"uppercase",color:"rgba(255,255,255,.5)",...DS,marginBottom:6}}>Hotel Price</div>
        {hotelPP>0
          ?<>
            <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:5}}>
              <span style={{fontSize:42,fontWeight:900,lineHeight:1,...PJS}}>£{hotelPP.toLocaleString()}</span>
              <span style={{fontSize:13,color:"rgba(255,255,255,.6)",...DS}}>per person</span>
            </div>
            <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:7}}>
              <span style={{background:bclr.bg,borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:700,color:bclr.txt,...DS}}>{board||"Room Only"}</span>
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,.6)",...DS}}>
              {pax} guest{pax>1?"s":""}: <strong style={{color:"#fff"}}>£{total.toLocaleString()}</strong>
              {" "}· Deposit: <strong style={{color:"#6ee7b7"}}>£{deposit}pp</strong>
            </div>
          </>
          :<div style={{fontSize:14,color:"rgba(255,255,255,.65)",...DS}}>Select a room to see price</div>
        }
      </div>

      <div style={{padding:"16px 18px"}}>
        {/* Trip summary */}
        <div style={{background:"#f8fafc",border:"1px solid #f1f5f9",borderRadius:10,padding:"11px 14px",marginBottom:14}}>
          <div style={{fontWeight:800,fontSize:14,color:NAV,...PJS,marginBottom:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{name||"Hotel"}</div>
          <div style={{fontSize:12,color:"#475569",...DS,marginBottom:6,display:"flex",alignItems:"center",gap:5}}>
            <I.Cal s={11}/>{ci?fDS(ci):"-"} — {co?fDS(co):"-"} · {nights} night{nights>1?"s":""}
          </div>
          {board&&<BoardBadge board={board}/>}
        </div>

        {/* Price breakdown */}
        {hotelPP>0&&<div style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:9,overflow:"hidden",marginBottom:14}}>
          <div style={{padding:"7px 12px",borderBottom:"1px solid #e2e8f0",fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:".06em",...DS}}>Price includes</div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"8px 12px",fontSize:13,...DS}}><span style={{color:"#334155"}}>🏨 Hotel {nights}n · {board||"Room Only"}</span><span style={{fontWeight:700,color:NAV}}>£{hotelPP}pp</span></div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"9px 12px",background:"#fff",fontSize:14,borderTop:"2px solid #e2e8f0",...DS}}><span style={{fontWeight:800,color:NAV,...PJS}}>Total per person</span><span style={{fontWeight:900,color:NAV,...PJS}}>£{hotelPP}</span></div>
        </div>}

        {/* Trust badges */}
        <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:14}}>
          {["✓ Free cancellation options","✓ Best price guarantee","✓ Secure booking","✓ 24/7 support"].map((t,i)=>(
            <span key={i} style={{fontSize:10,color:"#065f46",background:"#d1fae5",borderRadius:5,padding:"3px 8px",fontWeight:600,...DS}}>{t}</span>
          ))}
        </div>

        {/* Book button */}
        <button onClick={onBook} style={{width:"100%",padding:13,background:NAV,color:"#fff",border:"none",borderRadius:10,fontSize:15,fontWeight:800,cursor:"pointer",...PJS,marginBottom:8,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
          onMouseEnter={e=>e.currentTarget.style.background="#1e3a5c"} onMouseLeave={e=>e.currentTarget.style.background=NAV}>
          {hotelPP?"Book Now":"Enquire"} <I.Right s={14} c="#fff"/>
        </button>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
          <a href="tel:02030190055" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"10px 0",background:"#f1f5f9",color:NAV,borderRadius:9,fontWeight:700,fontSize:12,textDecoration:"none",...DS,border:"1px solid #e2e8f0"}}><I.Phone s={12}/>Call us</a>
          <a href="https://wa.me/447487672075" target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"10px 0",background:"#22c55e",color:"#fff",borderRadius:9,fontWeight:700,fontSize:12,textDecoration:"none",...DS}}><I.WApp s={13}/>WhatsApp</a>
        </div>
        {deposit>0&&<div style={{textAlign:"center",fontSize:11,color:"#1e40af",...DS,background:"#eff6ff",borderRadius:7,padding:"7px 12px",fontWeight:600}}>Reserve with just £{deposit}pp deposit</div>}
      </div>
    </div>
  );
}

/* ── Amenities Grid ───────────────────────────────────────── */
function AmenitiesGrid({facs}){
  const [all,setAll]=useState(false);
  if(!facs?.length)return null;
  const items=facs[0]?.type?facs:facs.map(f=>({name:_s(f?.name||f),type:""}));
  const vis=all?items:items.slice(0,18);
  const ico=n=>{const k=(n||"").toLowerCase();if(k.includes("pool"))return"🏊";if(k.includes("wifi"))return"📶";if(k.includes("gym")||k.includes("fitness"))return"💪";if(k.includes("restaurant")||k.includes("dining"))return"🍽️";if(k.includes("spa")||k.includes("sauna"))return"🧖";if(k.includes("park"))return"🅿️";if(k.includes("air"))return"❄️";if(k.includes("bar"))return"🍸";return"✓";};
  return <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:12,padding:"18px 20px",marginTop:12}}>
    <h3 style={{fontSize:14,fontWeight:800,margin:"0 0 14px",color:NAV,...PJS}}>Hotel Facilities</h3>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:"6px 10px"}}>
      {vis.map((f,i)=><div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#334155",...DS}}><span style={{fontSize:13}}>{ico(f.name)}</span>{f.name}</div>)}
    </div>
    {items.length>18&&<button onClick={()=>setAll(v=>!v)} style={{marginTop:12,padding:"5px 14px",background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:20,fontSize:12,cursor:"pointer",color:NAV,fontFamily:"inherit",...DS}}>{all?"Less ↑":`All ${items.length} facilities ↓`}</button>}
  </div>;
}

/* ══════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════ */
export default function HotelDetails(){
  const {hotelId} = useParams();
  const [sp]      = useSearchParams();
  const nav       = useNavigate();

  const ci           = sp.get("checkIn")   || toISO(addD(new Date(),30));
  const co           = sp.get("checkOut")  || toISO(addD(new Date(),37));
  const guests       = Number(sp.get("guests")) || 2;
  const rooms        = Number(sp.get("rooms"))  || 1;
  const boardFromUrl = sp.get("board") || "";
  const cityParam    = sp.get("city")  || "";
  const nameFromUrl  = sp.get("hotelName") || "";
  const priceFromUrl = Number(sp.get("price")) || 0;
  const nights       = Math.max(1,Math.round((new Date(co)-new Date(ci))/86400000));
  const pax          = guests*rooms;

  const [det,      setDet]    = useState(null);
  const [photos,   setPhotos] = useState([]);
  const [desc,     setDesc]   = useState("");
  const [roomList, setRooms]  = useState([]);
  const [roomLoad, setRoomLd] = useState(true);
  const [selRoom,  setSel]    = useState(null);
  const [facsList, setFacs]   = useState([]);
  const [policies, setPols]   = useState({});
  const [reviews,  setRevs]   = useState(null);
  const [revLoad,  setRevLd]  = useState(false);
  const [revScores,setRevScores] = useState(null);
  const [cars,     setCars]   = useState([]);
  const [taxis,    setTaxis]  = useState([]);
  const [attracts, setAttracts] = useState([]);
  const [extrasLoad,setExtLd] = useState(false);
  const [loading,  setLoading]= useState(true);
  const [error,    setError]  = useState("");
  const [tab,      setTab]    = useState("overview");
  const [detCity,  setDetCity]= useState("");
  const [detName,  setDetName]= useState(nameFromUrl);
  const [hotelPP,  setHotelPP]= useState(priceFromUrl||null);

  /* Hotel details + photos */
  useEffect(()=>{
    if(!hotelId)return;
    let gone=false;
    setLoading(true);setError("");
    Promise.all([getHotelDetails(hotelId),getHotelPhotos(hotelId)])
      .then(([d,pho])=>{
        if(gone)return;
        setDet(d);
        const lc=_s(d?.city||d?.cityName||d?.district||"");if(lc)setDetCity(lc);
        const ln=_s(d?.hotel_name||d?.name||d?.name_trans||d?.HotelName||d?.hotelName||d?.title||d?.property?.name||"");if(ln)setDetName(ln);
        if(Array.isArray(pho)&&pho.length)setPhotos(pho);
        else setPhotos([d?.main_photo_url,d?.max_photo_url].filter(Boolean).map(u=>fixPhoto(u)||u).filter(Boolean));
        setLoading(false);
        const fd=extractDescription(d);
        if(fd&&fd.length>20)setDesc(fd);else getDescriptionAndInfo(hotelId).then(t=>{if(!gone&&t?.length>20)setDesc(t);}).catch(()=>{});
        getHotelFacilities(hotelId).then(f=>{if(!gone&&f?.length)setFacs(f);}).catch(()=>{});
        getHotelPolicies(hotelId).then(p=>{if(!gone)setPols(p);}).catch(()=>{});
        getHotelReviewScores(hotelId).then(s=>{if(!gone&&s?.overall>0)setRevScores(s);}).catch(()=>{});
      })
      .catch(e=>{if(!gone){setError(e.message||"Failed");setLoading(false);}});
    return()=>{gone=true;};
  },[hotelId]);

  /* Rooms */
  useEffect(()=>{
    if(!hotelId||!ci||!co)return;
    let gone=false;
    setRoomLd(true);setRooms([]);setSel(null);
    getRoomsWithAvailability(hotelId,ci,co,guests,rooms)
      .then(({rooms:rms,source:src})=>{
        if(gone)return;
        const raw=Array.isArray(rms)?rms:[];
        const norm=raw.map(r=>({
          ...r,
          _name:  _s(r?.room_name||r?.name||r?.title||"Standard Room"),
          _price: extractPrice(r),
          _board: detectBoard(r)||boardFromUrl||"Room Only",
          _photos:[...(r?.photos||r?.room_photos||[]).map(p=>fixPhoto(_s(p?.url_original||p?.url||""))),
                   ...(r?.main_photo_url?[fixPhoto(r.main_photo_url)||r.main_photo_url]:[])].filter(Boolean),
          _beds:  _s(r?.bed_configurations?.[0]?.bed_types?.[0]?.name_with_count||r?.bed_types?.[0]?.description||r?.bedding||""),
          _maxOcc:Number(r?.nr_adults||r?.max_occupancy||r?.occupancy?.max||0),
          _extras:[...(r?.facilities||r?.room_facilities||r?.amenities||[]).slice(0,5).map(f=>_s(f?.name||f?.facility_name||f))].filter(Boolean),
        }));
        setRooms(norm);setRoomLd(false);
        if(norm.length>0){
          const withP=norm.filter(r=>r._price>0);
          const cheapest=withP.length>0?withP.reduce((a,b)=>a._price<=b._price?a:b):norm[0];
          setSel(cheapest);
          if(cheapest._price>0)setHotelPP(Math.ceil((cheapest._price/Math.max(1,guests))*1.05));
        }
      })
      .catch(e=>{if(!gone){console.warn("Rooms:",e);setRoomLd(false);}});
    return()=>{gone=true;};
  },[hotelId,ci,co,boardFromUrl]);

  useEffect(()=>{
    if(!selRoom)return;
    if(selRoom._price>0)setHotelPP(Math.ceil((selRoom._price/Math.max(1,guests))*1.05));
  },[selRoom]);

  /* Load extras when tab opened */
  const loadExtras=useCallback(()=>{
    if(cars.length||taxis.length||attracts.length||extrasLoad)return;
    const dest=cityParam||detCity||"";if(!dest)return;
    setExtLd(true);
    Promise.allSettled([
      searchCarLocation(dest).then(locs=>{const id=locs?.[0]?.id||locs?.[0]?.dest_id;return id?searchCarRentals({pickUpId:id,dropOffId:id,pickUpDate:ci,dropOffDate:co}):[];}).then(list=>setCars(list||[])).catch(()=>{}),
      searchTaxiLocation(dest).then(locs=>{const id=locs?.[0]?.id||locs?.[0]?.dest_id;return id?searchTaxis({pickUpId:id,dropOffId:id,pickUpDate:ci}):[];}).then(list=>setTaxis(list||[])).catch(()=>{}),
      searchAttractionLocation(dest).then(locs=>{const id=locs?.[0]?.id||locs?.[0]?.dest_id;return id?searchAttractions({id,startDate:ci,endDate:co,adults:guests}):[];}).then(list=>setAttracts(list||[])).catch(()=>{}),
    ]).finally(()=>setExtLd(false));
  },[cars,taxis,attracts,extrasLoad,cityParam,detCity,ci,co,guests]);

  const loadRevs=useCallback(()=>{
    if(reviews||revLoad)return;
    setRevLd(true);
    getHotelReviews(hotelId).then(r=>{setRevs(r);setRevLd(false);}).catch(()=>setRevLd(false));
  },[hotelId,reviews,revLoad]);

  const hotelName=(()=>{const n=extractName(det)||detName;return(n&&n.length>2&&isNaN(Number(n)))?n:(detName||extractName(det)||"");})();
  const city      = _s(det?.city||det?.cityName||"");
  const lat       = det?.latitude, lng=det?.longitude;
  const stars     = Number(det?.class||det?.stars||0);
  const rating    = Number(det?.review_score||det?.reviewScore||0);
  const revCnt    = Number(det?.review_nr||det?.reviewCount||0);
  const displayBd = (()=>{if(selRoom?._board&&selRoom._board.length>2)return selRoom._board;if(boardFromUrl&&boardFromUrl.length>2&&boardFromUrl!=="null")return boardFromUrl;return"Room Only";})();
  const effHP     = hotelPP||null;

  const goBook=useCallback(()=>{
    nav(`/checkout?hotelName=${encodeURIComponent(hotelName)}&hotelId=${hotelId}&checkIn=${ci}&checkOut=${co}&guests=${guests}&rooms=${rooms}&price=${effHP||""}&board=${encodeURIComponent(displayBd)}`);
  },[hotelName,hotelId,ci,co,guests,rooms,effHP,displayBd]);

  const facsDisplay=useMemo(()=>{
    if(facsList.length>0)return facsList;
    if(!det)return[];
    const srcs=[det?.facilities_block?.facilities,det?.facilities,det?.top_ufi_benefits,det?.hotel_facilities];
    const out=new Set();
    srcs.forEach(s=>{(Array.isArray(s)?s:[]).forEach(x=>{const n=_s(x?.name||x?.facility_name||x?.translated_name||x?.text||x);if(n&&n.length>1&&n.length<60)out.add(n);});});
    return[...out].slice(0,30).map(n=>({name:n,type:""}));
  },[det,facsList]);

  if(loading) return(<>
    <Navbar/>
    <div style={{maxWidth:1300,margin:"0 auto",padding:"20px 16px"}}>
      <div className="hd-grid" style={{display:"grid",gridTemplateColumns:"1fr 380px",gap:22}}>
        <div><Sk h={450} r={12} mb={12}/><Sk h={15} w="55%" mb={8}/><Sk h={13} w="40%"/></div>
        <Sk h={560} r={14}/>
      </div>
    </div>
    <Footer/>
    <style>{"@keyframes sk{0%{background-position:200% 0}100%{background-position:-200% 0}}"}</style>
  </>);

  if(error||!det) return(<>
    <Navbar/>
    <div style={{maxWidth:500,margin:"80px auto",textAlign:"center",padding:"0 20px"}}>
      <div style={{fontSize:48,marginBottom:14}}>😕</div>
      <h2 style={{...PJS,color:NAV}}>Hotel not found</h2>
      <p style={{color:"#64748b",...DS,marginBottom:22}}>{error||"Could not load hotel details"}</p>
      <button onClick={()=>nav(-1)} style={{padding:"11px 24px",background:NAV,color:"#fff",border:"none",borderRadius:9,fontWeight:700,cursor:"pointer",fontFamily:"inherit",...DS}}>← Back</button>
    </div>
    <Footer/>
  </>);

  const TABS=[["overview","Overview"],["rooms","Choose Room"],["facilities","Facilities"],["extras","Extras 🚗"],["location","Location"],["policies","Policies"],["reviews","Reviews"]];

  return(<>
    <Navbar/>

    {/* Breadcrumb */}
    <div style={{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"9px 20px"}}>
      <div style={{maxWidth:1300,margin:"0 auto",display:"flex",gap:6,alignItems:"center",fontSize:12,...DS}}>
        <Link to="/" style={{color:GRN,textDecoration:"none",fontWeight:600}}>Home</Link>
        <span style={{color:"#d1d5db"}}>/</span>
        {city&&<><Link to={`/hotels?city=${encodeURIComponent(city)}&checkIn=${ci}&checkOut=${co}&guests=${guests}`} style={{color:GRN,textDecoration:"none",fontWeight:600}}>{city}</Link><span style={{color:"#d1d5db"}}>/</span></>}
        <span style={{color:"#94a3b8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:220}}>{hotelName}</span>
      </div>
    </div>

    <div style={{background:"#f3f6fb",minHeight:"100vh",paddingBottom:80}}>
      <div style={{maxWidth:1300,margin:"0 auto",padding:"18px 16px"}}>
        <div className="hd-grid" style={{display:"grid",gridTemplateColumns:"1fr 380px",gap:22,alignItems:"flex-start"}}>

          {/* LEFT */}
          <div style={{minWidth:0}}>
            {/* Header */}
            <div style={{background:"#fff",borderRadius:"14px 14px 0 0",border:"1px solid #e2e8f0",borderBottom:"none",padding:"18px 20px 14px"}}>
              {stars>0&&<div style={{display:"flex",gap:1,alignItems:"center",marginBottom:5}}>{[...Array(Math.min(5,Math.round(stars)))].map((_,i)=><I.Star key={i} s={15}/>)}<span style={{fontSize:11,color:"#94a3b8",marginLeft:5,...DS}}>{Math.round(stars)} star</span></div>}
              <h1 style={{fontSize:23,fontWeight:900,margin:"0 0 8px",color:NAV,...PJS}}>{hotelName}</h1>
              <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                {city&&<span style={{display:"flex",alignItems:"center",gap:4,fontSize:13,color:"#64748b",...DS}}><I.Pin s={11}/>{city}</span>}
                {rating>0&&<div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Bubbles score={rating} sz={11}/>
                  <span style={{background:"#006ce4",color:"#fff",borderRadius:"4px 4px 4px 0",padding:"2px 8px",fontSize:12,fontWeight:800,...PJS}}>{rating.toFixed(1)}</span>
                  <span style={{fontSize:11,color:"#94a3b8",...DS}}>{revCnt>0?`${revCnt.toLocaleString()} reviews`:ratingLabel(rating)}</span>
                </div>}
                <BoardBadge board={displayBd}/>
              </div>
            </div>

            {/* Gallery */}
            <div style={{background:"#fff",border:"1px solid #e2e8f0",borderTop:"none",padding:"0 20px 16px"}}>
              <Gallery photos={photos} name={hotelName}/>
            </div>

            {/* Tabs */}
            <div style={{background:"#fff",border:"1px solid #e2e8f0",borderTop:"none",borderRadius:"0 0 14px 14px",overflow:"hidden"}}>
              <div style={{display:"flex",overflowX:"auto",scrollbarWidth:"none",borderBottom:"1px solid #f1f5f9"}}>
                {TABS.map(([id,lbl])=>(
                  <button key={id} onClick={()=>{setTab(id);if(id==="reviews"&&!reviews)loadRevs();if(id==="extras")loadExtras();}}
                    style={{flexShrink:0,padding:"12px 16px",border:"none",background:tab===id?"#fff":"#fafbfc",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",color:tab===id?NAV:"#64748b",borderBottom:tab===id?`2.5px solid ${NAV}`:"2.5px solid transparent",whiteSpace:"nowrap"}}>
                    {lbl}
                    {id==="rooms"&&<span style={{marginLeft:5,background:roomList.length>0?NAV:"#d1d5db",color:"#fff",borderRadius:10,fontSize:10,padding:"1px 6px",...DS}}>{roomLoad?"…":roomList.length}</span>}
                  </button>
                ))}
              </div>

              <div style={{padding:"20px"}}>
                {/* OVERVIEW */}
                {tab==="overview"&&(<>
                  {desc
                    ?<p style={{fontSize:14,color:"#334155",lineHeight:1.9,margin:"0 0 18px",...DS}}>{desc}</p>
                    :<p style={{color:"#94a3b8",fontSize:13,...DS,margin:"0 0 18px"}}>Contact us for full hotel details.</p>
                  }
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:8}}>
                    {[
                      {ico:"📅",lbl:"Check-in", val:fDS(ci)||"-"},
                      {ico:"📅",lbl:"Check-out",val:fDS(co)||"-"},
                      {ico:"🌙",lbl:"Duration", val:`${nights}n`},
                      {ico:"👥",lbl:"Guests",   val:`${guests} adult${guests>1?"s":""}`},
                      ...(stars>0?[{ico:"⭐",lbl:"Stars",  val:`${Math.round(stars)}★`}]:[]),
                      ...(rating>0?[{ico:"💬",lbl:"Rating", val:`${rating.toFixed(1)}/10`}]:[]),
                    ].map((c,i)=>(
                      <div key={i} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:9,padding:"10px 12px"}}>
                        <div style={{fontSize:17,marginBottom:2}}>{c.ico}</div>
                        <div style={{fontSize:9,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".05em",...DS,marginBottom:1}}>{c.lbl}</div>
                        <div style={{fontSize:13,fontWeight:700,color:NAV,...DS}}>{c.val}</div>
                      </div>
                    ))}
                  </div>
                </>)}

                {/* ROOMS */}
                {tab==="rooms"&&(<div>
                  <div style={{marginBottom:14}}>
                    <h3 style={{fontSize:15,fontWeight:800,...PJS,margin:"0 0 4px",color:NAV}}>Choose your room</h3>
                    <p style={{fontSize:13,color:"#64748b",...DS,margin:0}}>Hotel-only pricing — per person</p>
                  </div>
                  {roomLoad&&<div style={{display:"flex",flexDirection:"column",gap:8}}>{[1,2,3].map(i=><div key={i} style={{height:120,borderRadius:10,background:"linear-gradient(90deg,#f1f5f9 25%,#e8edf4 50%,#f1f5f9 75%)",backgroundSize:"200% 100%",animation:"sk 1.5s infinite"}}/>)}</div>}
                  {!roomLoad&&roomList.length===0&&(
                    <div style={{textAlign:"center",padding:"36px 20px",background:"#fff7ed",borderRadius:12,border:"1px solid #fed7aa"}}>
                      <div style={{fontSize:36,marginBottom:10}}>🛎️</div>
                      <p style={{fontWeight:800,color:NAV,...PJS,marginBottom:6,fontSize:15}}>Check availability with our team</p>
                      <p style={{fontSize:13,color:"#78350f",...DS,marginBottom:16}}>Room options available on request for your selected dates</p>
                      <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                        <a href="tel:02030190055" style={{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 18px",background:NAV,color:"#fff",borderRadius:8,fontWeight:700,fontSize:13,textDecoration:"none",...DS}}><I.Phone s={12}/>020 3019 0055</a>
                        <a href="https://wa.me/447487672075" target="_blank" rel="noreferrer" style={{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 18px",background:"#22c55e",color:"#fff",borderRadius:8,fontWeight:700,fontSize:13,textDecoration:"none",...DS}}><I.WApp s={13}/>WhatsApp Us</a>
                      </div>
                    </div>
                  )}
                  {!roomLoad&&roomList.length>0&&(<>
                    {roomList.slice(0,8).map((r,i)=><RoomCard key={i} room={r} selected={selRoom===r} onSelect={()=>setSel(r)} nights={nights} guests={guests} boardFromUrl={boardFromUrl}/>)}
                    {selRoom&&<button onClick={goBook} style={{width:"100%",padding:"13px 0",background:GRN,color:"#fff",border:"none",borderRadius:10,fontWeight:800,fontSize:14,cursor:"pointer",...PJS,marginTop:6}}>
                      Book {selRoom._name} — from £{effHP||"?"}pp
                    </button>}
                  </>)}
                </div>)}

                {/* FACILITIES */}
                {tab==="facilities"&&(facsDisplay.length>0?(()=>{
                  const g={};facsDisplay.forEach(f=>{const t=f.type||"General";if(!g[t])g[t]=[];g[t].push(f.name||_s(f));});
                  return Object.entries(g).map(([type,items])=>(
                    <div key={type} style={{marginBottom:14}}>
                      {type&&type!=="General"&&<div style={{fontSize:11,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:7,...DS}}>{type}</div>}
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:5}}>
                        {items.map((f,i)=><div key={i} style={{display:"flex",alignItems:"center",gap:6,background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:7,padding:"6px 10px",fontSize:12,...DS}}><span style={{color:GRN}}>✓</span>{f}</div>)}
                      </div>
                    </div>
                  ));
                })():<p style={{color:"#94a3b8",fontSize:13,...DS}}>Loading facilities…</p>)}

                {/* EXTRAS */}
                {tab==="extras"&&(<div>
                  {extrasLoad&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"20px 0",color:"#64748b",...DS}}><div style={{width:14,height:14,border:"2px solid #e2e8f0",borderTopColor:GRN,borderRadius:"50%",animation:"spin .7s linear infinite"}}/>Loading extras…</div>}
                  {!extrasLoad&&cars.length===0&&taxis.length===0&&attracts.length===0&&(
                    <div style={{textAlign:"center",padding:"32px 20px",color:"#94a3b8"}}>
                      <div style={{fontSize:40,marginBottom:10}}>🔍</div>
                      <p style={{fontSize:14,...DS}}>No extras found for this destination. <a href="tel:02030190055" style={{color:"#2563eb",fontWeight:700,textDecoration:"none"}}>Call us</a> to arrange transfers or activities.</p>
                    </div>
                  )}
                  {/* Transfers */}
                  {taxis.length>0&&<div style={{marginBottom:20}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}><span style={{fontSize:20}}>🚖</span><h3 style={{fontSize:14,fontWeight:800,color:NAV,...PJS,margin:0}}>Airport Transfers</h3></div>
                    {taxis.slice(0,4).map((t,i)=>{
                      const name=_s(t?.vehicle_name||t?.name||"Transfer");
                      const price=Number(t?.price?.gross||t?.price?.amount||t?.min_price||t?.price||0);
                      const cap=Number(t?.max_passengers||t?.capacity||4);
                      return <div key={i} style={{display:"flex",gap:12,border:"1px solid #e2e8f0",borderRadius:10,padding:"12px",marginBottom:8,background:"#fff",alignItems:"center"}}>
                        <span style={{fontSize:28,flexShrink:0}}>🚖</span>
                        <div style={{flex:1}}><div style={{fontWeight:700,fontSize:13,color:NAV,...DS,marginBottom:2}}>{name}</div><div style={{fontSize:11,color:"#64748b",...DS}}>Up to {cap} passengers</div></div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          {price>0?<><div style={{fontSize:18,fontWeight:900,color:NAV,...PJS}}>£{Math.ceil(price)}</div><div style={{fontSize:10,color:"#64748b",...DS}}>per booking</div></>:<div style={{fontSize:12,color:"#94a3b8",...DS}}>Call for price</div>}
                          <a href="tel:02030190055" style={{display:"inline-block",marginTop:5,padding:"4px 10px",background:NAV,color:"#fff",borderRadius:6,fontSize:11,fontWeight:700,textDecoration:"none",...DS}}>Book</a>
                        </div>
                      </div>;
                    })}
                  </div>}
                  {/* Cars */}
                  {cars.length>0&&<div style={{marginBottom:20}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}><span style={{fontSize:20}}>🚗</span><h3 style={{fontSize:14,fontWeight:800,color:NAV,...PJS,margin:0}}>Car Hire</h3></div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:8}}>
                      {cars.slice(0,6).map((c,i)=>{
                        const name=_s(c?.vehicle_info?.v_name||c?.name||"Car");
                        const price=Number(c?.pricing_info?.drive_away_price||c?.price?.gross||c?.min_price||0);
                        const img=c?.vehicle_info?.image_thumbnail_url||c?.image_url||null;
                        return <div key={i} style={{border:"1px solid #e2e8f0",borderRadius:10,overflow:"hidden",background:"#fff"}}>
                          <div style={{height:90,background:"#f8fafc",display:"flex",alignItems:"center",justifyContent:"center",padding:"8px",borderBottom:"1px solid #f1f5f9"}}>
                            {img?<img src={img} alt={name} style={{maxHeight:"100%",maxWidth:"100%",objectFit:"contain"}} onError={e=>e.target.parentElement.innerHTML='<span style="font-size:28px">🚗</span>'}/>:<span style={{fontSize:28}}>🚗</span>}
                          </div>
                          <div style={{padding:"10px 12px"}}>
                            <div style={{fontWeight:700,fontSize:12,color:NAV,...DS,marginBottom:8}}>{name}</div>
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                              {price>0?<div style={{fontSize:15,fontWeight:900,color:NAV,...PJS}}>£{Math.ceil(price)}</div>:<div style={{fontSize:11,color:"#94a3b8",...DS}}>Price TBC</div>}
                              <a href="tel:02030190055" style={{padding:"5px 10px",background:GRN,color:"#fff",borderRadius:6,fontSize:11,fontWeight:700,textDecoration:"none",...DS}}>Book</a>
                            </div>
                          </div>
                        </div>;
                      })}
                    </div>
                  </div>}
                  {/* Attractions */}
                  {attracts.length>0&&<div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}><span style={{fontSize:20}}>🎡</span><h3 style={{fontSize:14,fontWeight:800,color:NAV,...PJS,margin:0}}>Things to Do</h3></div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8}}>
                      {attracts.slice(0,8).map((a,i)=>{
                        const name=_s(a?.name||a?.title||"Attraction");
                        const price=Number(a?.representativePrice?.publicAmount||a?.price?.amount||a?.min_price||0);
                        const img=a?.primaryPhoto?.small||a?.image_url||null;
                        const rat=Number(a?.reviewsStats?.allReviewsCount>0?a?.reviewsStats?.combinedNumericStats?.average:0);
                        return <div key={i} style={{border:"1px solid #e2e8f0",borderRadius:10,overflow:"hidden",background:"#fff"}}>
                          <div style={{height:100,background:"#f1f5f9",overflow:"hidden"}}>
                            {img?<img src={img} alt={name} style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>:<div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>🎡</div>}
                          </div>
                          <div style={{padding:"10px 12px"}}>
                            <div style={{fontWeight:700,fontSize:12,color:NAV,...DS,marginBottom:4,lineHeight:1.3}}>{name}</div>
                            {rat>0&&<div style={{fontSize:10,color:"#d97706",marginBottom:5,...DS}}>★ {rat.toFixed(1)}</div>}
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                              {price>0?<div style={{fontSize:14,fontWeight:900,color:NAV,...PJS}}>£{Math.ceil(price)}<span style={{fontSize:9,color:"#94a3b8",...DS}}> pp</span></div>:<div style={{fontSize:11,color:"#94a3b8",...DS}}>See prices</div>}
                              <a href="tel:02030190055" style={{padding:"4px 9px",background:NAV,color:"#fff",borderRadius:6,fontSize:11,fontWeight:700,textDecoration:"none",...DS}}>Book</a>
                            </div>
                          </div>
                        </div>;
                      })}
                    </div>
                  </div>}
                </div>)}

                {/* LOCATION */}
                {tab==="location"&&(lat&&lng?<>
                  <div style={{borderRadius:10,overflow:"hidden",height:360,marginBottom:10}}>
                    <iframe title="map" src={`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`} width="100%" height="360" style={{border:0,display:"block"}} allowFullScreen loading="lazy"/>
                  </div>
                  <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer" style={{fontSize:13,color:"#2563eb",fontWeight:600,textDecoration:"none",...DS}}>Open in Google Maps →</a>
                </>:<p style={{color:"#94a3b8",fontSize:13,...DS}}>Location not available</p>)}

                {/* POLICIES */}
                {tab==="policies"&&(Object.keys(policies).length>0
                  ?Object.entries(policies).filter(([,v])=>typeof v==="string"&&v.length>5&&!v.startsWith("[{")).map(([k,v])=>(
                    <div key={k} style={{marginBottom:14,paddingBottom:14,borderBottom:"1px solid #f1f5f9"}}>
                      <div style={{fontWeight:700,fontSize:13,color:NAV,...PJS,marginBottom:4}}>{k}</div>
                      <p style={{fontSize:13,color:"#475569",lineHeight:1.8,margin:0,...DS}}>{v}</p>
                    </div>
                  )):<p style={{color:"#94a3b8",fontSize:13,...DS}}>Contact us for policy details.</p>)}

                {/* REVIEWS */}
                {tab==="reviews"&&(<>
                  {revLoad&&<p style={{color:"#64748b",...DS}}>Loading…</p>}
                  {!reviews&&!revLoad&&<button onClick={loadRevs} style={{background:"#f0fdf4",color:"#065f46",border:"1px solid #a7f3d0",padding:"10px 22px",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>Load Guest Reviews</button>}
                  {reviews&&(<>
                    {reviews.avgScore>0&&<div style={{background:"#f0fdf4",borderRadius:12,padding:"14px 18px",marginBottom:16}}>
                      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:revScores&&Object.keys(revScores.scores||{}).length>0?12:0}}>
                        <div style={{fontSize:38,fontWeight:900,color:"#065f46",...PJS}}>{reviews.avgScore.toFixed(1)}</div>
                        <div><div style={{fontWeight:800,fontSize:14,...PJS,color:"#065f46"}}>{ratingLabel(reviews.avgScore)}</div><Bubbles score={reviews.avgScore} sz={12}/><div style={{fontSize:11,color:"#64748b",marginTop:2,...DS}}>{reviews.total.toLocaleString()} reviews</div></div>
                      </div>
                      {revScores&&Object.keys(revScores.scores||{}).length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:6}}>
                        {Object.entries(revScores.scores).slice(0,6).map(([k,v])=>(
                          <div key={k} style={{background:"#fff",borderRadius:8,padding:"8px 10px"}}>
                            <div style={{fontSize:11,color:"#64748b",marginBottom:3,...DS}}>{k}</div>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <div style={{flex:1,height:4,background:"#e2e8f0",borderRadius:2,overflow:"hidden"}}><div style={{width:`${Math.min(100,(v/10)*100)}%`,height:"100%",background:v>=8?"#059669":v>=7?"#d97706":"#dc2626",borderRadius:2}}/></div>
                              <span style={{fontSize:12,fontWeight:700,color:"#0f172a",...DS}}>{v.toFixed(1)}</span>
                            </div>
                          </div>
                        ))}
                      </div>}
                    </div>}
                    {reviews.items.slice(0,6).map((rv,i)=>{
                      const str=v=>v&&typeof v==="object"?_s(v):String(v||"");
                      const sc=parseFloat(rv?.reviewer_score||rv?.rating||0)||0;
                      const auth=str(rv?.reviewer?.name||rv?.reviewer_name||rv?.reviewer||rv?.author)||"Guest";
                      const pros=str(rv?.pros||rv?.review_text||rv?.text||"");
                      const cons=str(rv?.cons||"");
                      return <div key={i} style={{border:"1px solid #e2e8f0",borderRadius:10,padding:"12px 14px",marginBottom:8}}>
                        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:7}}>
                          <span style={{background:"#006ce4",color:"#fff",borderRadius:"5px 5px 5px 0",padding:"2px 8px",fontWeight:900,fontSize:13,...PJS}}>{sc.toFixed(1)}</span>
                          <span style={{fontWeight:700,fontSize:12,...DS,color:NAV}}>{auth}</span>
                        </div>
                        {pros&&<p style={{fontSize:13,color:"#334155",margin:"0 0 3px",lineHeight:1.65,...DS}}><span style={{color:GRN,fontWeight:700}}>+ </span>{pros}</p>}
                        {cons&&<p style={{fontSize:13,color:"#334155",margin:0,lineHeight:1.65,...DS}}><span style={{color:"#dc2626",fontWeight:700}}>− </span>{cons}</p>}
                      </div>;
                    })}
                  </>)}
                </>)}
              </div>
            </div>
            <AmenitiesGrid facs={facsDisplay}/>
          </div>

          {/* RIGHT — sticky booking panel */}
          <div className="hd-sticky" style={{position:"sticky",top:78,minWidth:0}}>
            <BookingPanel
              name={hotelName} ci={ci} co={co} nights={nights}
              board={displayBd} hotelPP={effHP} pax={pax}
              onBook={goBook}
            />
          </div>
        </div>
      </div>
    </div>

    <Footer/>
    <style>{`
      @keyframes spin{to{transform:rotate(360deg)}}
      @keyframes sk{0%{background-position:200% 0}100%{background-position:-200% 0}}
      @media(max-width:960px){.hd-grid{grid-template-columns:1fr!important}.hd-sticky{position:relative!important;top:auto!important}}
    `}</style>
  </>);
}